import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Target, LogIn, UserPlus, Clock, CheckCircle, User, Hash, KeyRound, ArrowLeft } from "lucide-react";
import axios from "axios";

const API = '/api';

export default function Login() {
  const navigate = useNavigate();
  const { login, register, shooterLogin } = useAuth();
  const [loading, setLoading] = useState(false);
  const [pendingApproval, setPendingApproval] = useState(false);
  const [shooterCreated, setShooterCreated] = useState(false);
  const [settings, setSettings] = useState({ organization_name: "Trap Tracker", logo_url: null });
  
  // Forgot password/PIN states
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showForgotPin, setShowForgotPin] = useState(false);
  const [forgotPasswordStep, setForgotPasswordStep] = useState(1); // 1: enter email, 2: enter code + new password
  const [forgotPinStep, setForgotPinStep] = useState(1); // 1: enter shooter id, 2: enter code + new pin
  const [forgotPasswordData, setForgotPasswordData] = useState({ email: "", resetCode: "", newPassword: "", confirmPassword: "" });
  const [forgotPinData, setForgotPinData] = useState({ shooterId: "", email: "", resetCode: "", newPin: "", confirmPin: "" });
  
  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [shooterLoginData, setShooterLoginData] = useState({ shooterId: "", pin: "" });
  const [registerData, setRegisterData] = useState({ 
    name: "", email: "", phone: "", password: "", confirmPassword: "",
    registerAsShooter: true, shooterIdNumber: "", pin: ""
  });

  // Fetch settings for branding
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API}/settings`);
        setSettings(res.data);
      } catch (error) {
        console.error("Failed to fetch settings:", error);
      }
    };
    fetchSettings();
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(loginData.email, loginData.password);
      toast.success("Welcome back!");
      navigate("/home");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleShooterLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await shooterLogin(shooterLoginData.shooterId, shooterLoginData.pin);
      toast.success("Welcome back!");
      navigate("/my-results");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (registerData.password !== registerData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    // Validate PIN if provided
    if (registerData.registerAsShooter && registerData.pin && !/^\d{4}$/.test(registerData.pin)) {
      toast.error("PIN must be exactly 4 digits");
      return;
    }
    setLoading(true);
    try {
      const result = await register(
        registerData.name, 
        registerData.email, 
        registerData.phone, 
        registerData.password,
        registerData.registerAsShooter,
        registerData.shooterIdNumber,
        registerData.pin
      );
      
      if (result?.status === 'approved') {
        setShooterCreated(true);
        toast.success(result.message || "Account created!");
      } else if (result?.pending || result?.status === 'pending') {
        setPendingApproval(true);
        toast.success("Registration submitted! Please wait for admin approval.");
      } else {
        toast.success("Account created successfully!");
        navigate("/home");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  // Forgot Password handlers
  const handleForgotPasswordRequest = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/forgot-password`, { email: forgotPasswordData.email });
      toast.success(res.data.message);
      // For testing, show the reset code if returned
      if (res.data.reset_code) {
        toast.info(`Reset code: ${res.data.reset_code}`, { duration: 10000 });
      }
      setForgotPasswordStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to request password reset");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (forgotPasswordData.newPassword !== forgotPasswordData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    if (forgotPasswordData.newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/reset-password`, {
        email: forgotPasswordData.email,
        reset_code: forgotPasswordData.resetCode,
        new_password: forgotPasswordData.newPassword
      });
      toast.success(res.data.message);
      setShowForgotPassword(false);
      setForgotPasswordStep(1);
      setForgotPasswordData({ email: "", resetCode: "", newPassword: "", confirmPassword: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  // Forgot PIN handlers
  const handleForgotPinRequest = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/forgot-pin`, { 
        shooter_id: forgotPinData.shooterId,
        email: forgotPinData.email || undefined
      });
      toast.success(res.data.message);
      // For testing, show the reset code if returned
      if (res.data.reset_code) {
        toast.info(`Reset code: ${res.data.reset_code}`, { duration: 10000 });
      }
      setForgotPinStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to request PIN reset");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPin = async (e) => {
    e.preventDefault();
    if (forgotPinData.newPin !== forgotPinData.confirmPin) {
      toast.error("PINs do not match");
      return;
    }
    if (!/^\d{4}$/.test(forgotPinData.newPin)) {
      toast.error("PIN must be exactly 4 digits");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/reset-pin`, {
        shooter_id: forgotPinData.shooterId,
        reset_code: forgotPinData.resetCode,
        new_pin: forgotPinData.newPin
      });
      toast.success(res.data.message);
      setShowForgotPin(false);
      setForgotPinStep(1);
      setForgotPinData({ shooterId: "", email: "", resetCode: "", newPin: "", confirmPin: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset PIN");
    } finally {
      setLoading(false);
    }
  };

  const closeForgotPassword = () => {
    setShowForgotPassword(false);
    setForgotPasswordStep(1);
    setForgotPasswordData({ email: "", resetCode: "", newPassword: "", confirmPassword: "" });
  };

  const closeForgotPin = () => {
    setShowForgotPin(false);
    setForgotPinStep(1);
    setForgotPinData({ shooterId: "", email: "", resetCode: "", newPin: "", confirmPin: "" });
  };

  return (
    <div className="min-h-screen bg-[#F8F7F4] flex items-center justify-center p-4">
      <Card className="w-full max-w-md" data-testid="login-page">
        <CardHeader className="text-center">
          {settings.logo_url ? (
            <img 
              src={settings.logo_url} 
              alt={settings.organization_name}
              className="mx-auto h-16 w-auto object-contain mb-4"
            />
          ) : (
            <div className="mx-auto w-16 h-16 bg-[#1B3B6F] rounded-full flex items-center justify-center mb-4">
              <Target className="text-white" size={32} />
            </div>
          )}
          <CardTitle className="font-serif text-2xl">{settings.organization_name}</CardTitle>
          <a href="https://traptracker.net" target="_blank" rel="noopener noreferrer" className="text-slate-400 text-sm hover:text-[#1B3B6F] transition-colors">Powered by Trap Tracker</a>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="login">
            <TabsList className="grid w-full grid-cols-3 mb-6">
              <TabsTrigger value="login" data-testid="login-tab">
                <LogIn size={16} className="mr-1" /> Admin
              </TabsTrigger>
              <TabsTrigger value="shooter" data-testid="shooter-tab">
                <User size={16} className="mr-1" /> Shooter
              </TabsTrigger>
              <TabsTrigger value="register" data-testid="register-tab">
                <UserPlus size={16} className="mr-1" /> Register
              </TabsTrigger>
            </TabsList>

            {/* Admin Login Tab */}
            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label htmlFor="login-email">Email or Username</Label>
                  <Input
                    id="login-email"
                    type="text"
                    data-testid="login-email"
                    value={loginData.email}
                    onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                    placeholder="your@email.com"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="login-password">Password</Label>
                  <Input
                    id="login-password"
                    type="password"
                    data-testid="login-password"
                    value={loginData.password}
                    onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                    placeholder="••••••••"
                    required
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-[#1B3B6F] hover:bg-[#1B3B6F]/90"
                  disabled={loading}
                  data-testid="login-submit"
                >
                  {loading ? "Logging in..." : "Login"}
                </Button>
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="w-full text-center text-sm text-[#1B3B6F] hover:underline"
                  data-testid="forgot-password-link"
                >
                  Forgot Password?
                </button>
              </form>
            </TabsContent>

            {/* Shooter Login Tab */}
            <TabsContent value="shooter">
              <form onSubmit={handleShooterLogin} className="space-y-4">
                <div className="text-center mb-4">
                  <p className="text-sm text-slate-500">
                    Login with your Shooter ID to view your results
                  </p>
                </div>
                <div>
                  <Label htmlFor="shooter-id" className="flex items-center gap-1">
                    <Hash size={14} />
                    Shooter ID
                  </Label>
                  <Input
                    id="shooter-id"
                    type="text"
                    data-testid="shooter-login-id"
                    value={shooterLoginData.shooterId}
                    onChange={(e) => setShooterLoginData({ ...shooterLoginData, shooterId: e.target.value })}
                    placeholder="e.g., 12345"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="shooter-pin">PIN (if set)</Label>
                  <Input
                    id="shooter-pin"
                    type="password"
                    data-testid="shooter-login-pin"
                    value={shooterLoginData.pin}
                    onChange={(e) => setShooterLoginData({ ...shooterLoginData, pin: e.target.value })}
                    placeholder="4-digit PIN"
                    maxLength={4}
                    pattern="\d{4}"
                  />
                  <p className="text-xs text-slate-400 mt-1">
                    Leave blank if you haven't set a PIN
                  </p>
                </div>
                <Button
                  type="submit"
                  className="w-full bg-[#1B3B6F] hover:bg-[#1B3B6F]/90"
                  disabled={loading}
                  data-testid="shooter-login-submit"
                >
                  {loading ? "Logging in..." : "Login as Shooter"}
                </Button>
                <button
                  type="button"
                  onClick={() => setShowForgotPin(true)}
                  className="w-full text-center text-sm text-[#1B3B6F] hover:underline"
                  data-testid="forgot-pin-link"
                >
                  Forgot PIN?
                </button>
              </form>
            </TabsContent>

            {/* Register Tab */}
            <TabsContent value="register">
              {shooterCreated ? (
                <div className="text-center py-8">
                  <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                    <CheckCircle className="text-green-600" size={32} />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">Account Created!</h3>
                  <p className="text-slate-500 text-sm mb-4">
                    Your shooter account has been created and is ready to use.
                  </p>
                  <Button
                    className="bg-[#1B3B6F] hover:bg-[#1B3B6F]/90"
                    onClick={() => {
                      setShooterCreated(false);
                      document.querySelector('[data-testid="shooter-tab"]')?.click();
                    }}
                  >
                    Login Now
                  </Button>
                </div>
              ) : pendingApproval ? (
                <div className="text-center py-8">
                  <div className="mx-auto w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mb-4">
                    <Clock className="text-amber-600" size={32} />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">Registration Submitted!</h3>
                  <p className="text-slate-500 text-sm mb-4">
                    Your account is pending approval by an administrator.
                  </p>
                  <p className="text-slate-400 text-xs">
                    You will receive an email once your account has been approved.
                  </p>
                  <Button
                    variant="outline"
                    className="mt-6"
                    onClick={() => setPendingApproval(false)}
                  >
                    Register Another Account
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleRegister} className="space-y-4">
                  <div>
                    <Label htmlFor="register-name">Full Name</Label>
                    <Input
                      id="register-name"
                      data-testid="register-name"
                      value={registerData.name}
                      onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                      placeholder="John Smith"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="register-email">Email</Label>
                    <Input
                      id="register-email"
                      type="email"
                      data-testid="register-email"
                      value={registerData.email}
                      onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                      placeholder="your@email.com"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="register-phone">Phone (optional)</Label>
                    <Input
                      id="register-phone"
                      type="tel"
                      data-testid="register-phone"
                      value={registerData.phone}
                      onChange={(e) => setRegisterData({ ...registerData, phone: e.target.value })}
                      placeholder="(555) 123-4567"
                    />
                  </div>
                  
                  {/* Shooter Registration Option */}
                  <div className="flex items-start space-x-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <Checkbox
                      id="register-as-shooter"
                      checked={registerData.registerAsShooter}
                      onCheckedChange={(checked) => setRegisterData({ ...registerData, registerAsShooter: checked })}
                    />
                    <div className="space-y-1">
                      <Label htmlFor="register-as-shooter" className="cursor-pointer font-medium">
                        Register as Shooter
                      </Label>
                      <p className="text-xs text-slate-500">
                        Shooter accounts can login with just Shooter ID and view personal results.
                      </p>
                    </div>
                  </div>
                  
                  {registerData.registerAsShooter && (
                    <>
                      <div>
                        <Label htmlFor="register-shooter-id" className="flex items-center gap-1">
                          <Hash size={14} />
                          Your Shooter ID
                        </Label>
                        <Input
                          id="register-shooter-id"
                          data-testid="register-shooter-id"
                          value={registerData.shooterIdNumber}
                          onChange={(e) => setRegisterData({ ...registerData, shooterIdNumber: e.target.value })}
                          placeholder="e.g., 12345"
                        />
                        <p className="text-xs text-slate-400 mt-1">
                          Enter your shooter ID to link your account to your profile.
                        </p>
                      </div>
                      <div>
                        <Label htmlFor="register-pin" className="flex items-center gap-1">
                          <User size={14} />
                          4-Digit PIN (optional)
                        </Label>
                        <Input
                          id="register-pin"
                          type="password"
                          data-testid="register-pin"
                          value={registerData.pin}
                          onChange={(e) => {
                            // Only allow digits and max 4 characters
                            const val = e.target.value.replace(/\D/g, '').slice(0, 4);
                            setRegisterData({ ...registerData, pin: val });
                          }}
                          placeholder="4-digit PIN"
                          maxLength={4}
                          pattern="\d{4}"
                        />
                        <p className="text-xs text-slate-400 mt-1">
                          Set a PIN for secure shooter login (recommended).
                        </p>
                      </div>
                    </>
                  )}
                  
                  <div>
                    <Label htmlFor="register-password">Password</Label>
                    <Input
                      id="register-password"
                      type="password"
                      data-testid="register-password"
                      value={registerData.password}
                      onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                      placeholder="••••••••"
                      required
                      minLength={6}
                    />
                  </div>
                  <div>
                    <Label htmlFor="register-confirm">Confirm Password</Label>
                    <Input
                      id="register-confirm"
                      type="password"
                      data-testid="register-confirm"
                      value={registerData.confirmPassword}
                      onChange={(e) => setRegisterData({ ...registerData, confirmPassword: e.target.value })}
                      placeholder="••••••••"
                      required
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-[#1B3B6F] hover:bg-[#1B3B6F]/90"
                    disabled={loading}
                    data-testid="register-submit"
                  >
                    {loading ? "Creating account..." : "Create Account"}
                  </Button>
                  <p className="text-xs text-slate-500 text-center mt-4">
                    {registerData.registerAsShooter 
                      ? "Shooter accounts can login with Shooter ID + PIN."
                      : "Admin accounts require approval before you can log in."}
                  </p>
                </form>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Forgot Password Dialog */}
      <Dialog open={showForgotPassword} onOpenChange={closeForgotPassword}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound size={20} />
              {forgotPasswordStep === 1 ? "Forgot Password" : "Reset Password"}
            </DialogTitle>
            <DialogDescription>
              {forgotPasswordStep === 1 
                ? "Enter your email to receive a password reset code."
                : "Enter the reset code and your new password."}
            </DialogDescription>
          </DialogHeader>
          
          {forgotPasswordStep === 1 ? (
            <form onSubmit={handleForgotPasswordRequest} className="space-y-4">
              <div>
                <Label htmlFor="forgot-email">Email Address</Label>
                <Input
                  id="forgot-email"
                  type="email"
                  value={forgotPasswordData.email}
                  onChange={(e) => setForgotPasswordData({ ...forgotPasswordData, email: e.target.value })}
                  placeholder="your@email.com"
                  required
                />
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={closeForgotPassword} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 bg-[#1B3B6F]" disabled={loading}>
                  {loading ? "Sending..." : "Send Reset Code"}
                </Button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <button
                type="button"
                onClick={() => setForgotPasswordStep(1)}
                className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
              >
                <ArrowLeft size={14} /> Back
              </button>
              <div>
                <Label htmlFor="reset-code">Reset Code</Label>
                <Input
                  id="reset-code"
                  type="text"
                  value={forgotPasswordData.resetCode}
                  onChange={(e) => setForgotPasswordData({ ...forgotPasswordData, resetCode: e.target.value })}
                  placeholder="6-digit code"
                  maxLength={6}
                  required
                />
              </div>
              <div>
                <Label htmlFor="new-password">New Password</Label>
                <Input
                  id="new-password"
                  type="password"
                  value={forgotPasswordData.newPassword}
                  onChange={(e) => setForgotPasswordData({ ...forgotPasswordData, newPassword: e.target.value })}
                  placeholder="••••••••"
                  minLength={6}
                  required
                />
              </div>
              <div>
                <Label htmlFor="confirm-new-password">Confirm New Password</Label>
                <Input
                  id="confirm-new-password"
                  type="password"
                  value={forgotPasswordData.confirmPassword}
                  onChange={(e) => setForgotPasswordData({ ...forgotPasswordData, confirmPassword: e.target.value })}
                  placeholder="••••••••"
                  required
                />
              </div>
              <Button type="submit" className="w-full bg-[#1B3B6F]" disabled={loading}>
                {loading ? "Resetting..." : "Reset Password"}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Forgot PIN Dialog */}
      <Dialog open={showForgotPin} onOpenChange={closeForgotPin}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound size={20} />
              {forgotPinStep === 1 ? "Forgot PIN" : "Reset PIN"}
            </DialogTitle>
            <DialogDescription>
              {forgotPinStep === 1 
                ? "Enter your Shooter ID to receive a PIN reset code."
                : "Enter the reset code and your new PIN."}
            </DialogDescription>
          </DialogHeader>
          
          {forgotPinStep === 1 ? (
            <form onSubmit={handleForgotPinRequest} className="space-y-4">
              <div>
                <Label htmlFor="forgot-shooter-id">Shooter ID</Label>
                <Input
                  id="forgot-shooter-id"
                  type="text"
                  value={forgotPinData.shooterId}
                  onChange={(e) => setForgotPinData({ ...forgotPinData, shooterId: e.target.value })}
                  placeholder="e.g., 12345"
                  required
                />
              </div>
              <div>
                <Label htmlFor="forgot-shooter-email">Email (optional, for verification)</Label>
                <Input
                  id="forgot-shooter-email"
                  type="email"
                  value={forgotPinData.email}
                  onChange={(e) => setForgotPinData({ ...forgotPinData, email: e.target.value })}
                  placeholder="your@email.com"
                />
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={closeForgotPin} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 bg-[#1B3B6F]" disabled={loading}>
                  {loading ? "Sending..." : "Send Reset Code"}
                </Button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleResetPin} className="space-y-4">
              <button
                type="button"
                onClick={() => setForgotPinStep(1)}
                className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
              >
                <ArrowLeft size={14} /> Back
              </button>
              <div>
                <Label htmlFor="reset-pin-code">Reset Code</Label>
                <Input
                  id="reset-pin-code"
                  type="text"
                  value={forgotPinData.resetCode}
                  onChange={(e) => setForgotPinData({ ...forgotPinData, resetCode: e.target.value })}
                  placeholder="6-digit code"
                  maxLength={6}
                  required
                />
              </div>
              <div>
                <Label htmlFor="new-pin">New PIN</Label>
                <Input
                  id="new-pin"
                  type="password"
                  value={forgotPinData.newPin}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 4);
                    setForgotPinData({ ...forgotPinData, newPin: val });
                  }}
                  placeholder="4-digit PIN"
                  maxLength={4}
                  required
                />
              </div>
              <div>
                <Label htmlFor="confirm-new-pin">Confirm New PIN</Label>
                <Input
                  id="confirm-new-pin"
                  type="password"
                  value={forgotPinData.confirmPin}
                  onChange={(e) => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 4);
                    setForgotPinData({ ...forgotPinData, confirmPin: val });
                  }}
                  placeholder="4-digit PIN"
                  maxLength={4}
                  required
                />
              </div>
              <Button type="submit" className="w-full bg-[#1B3B6F]" disabled={loading}>
                {loading ? "Resetting..." : "Reset PIN"}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
